import React from 'react';
import { 
  GraduationCap, 
  Cpu, 
  TrendingUp, 
  Users, 
  BookOpen,
  Stethoscope,
  BarChart3,
  Shield,
} from 'lucide-react';
import './TileGrid.css';

interface TileData {
  id: number;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  link: string;
  category: string;
  featured?: boolean;
  stats?: {
    value: string;
    label: string;
  };
}

const tilesData: TileData[] = [
  {
    id: 1,
    icon: <GraduationCap size={32} />,
    title: 'AI Teaching Assistant',
    subtitle: 'Enhance classroom productivity with intelligent lesson planning and student performance analytics.',
    link: '/teach-assist-app',
    category: 'EDUCATION',
    featured: true
  },
  {
    id: 2,
    icon: <Cpu size={32} />,
    title: 'Enterprise AI Solutions',
    subtitle: 'Custom machine learning models tailored to your business objectives and operational needs.',
    link: '/custom-ai',
    category: 'ENTERPRISE',
    stats: {
      value: '40%',
      label: 'Efficiency Gain'
    }
  },
  {
    id: 3,
    icon: <TrendingUp size={32} />,
    title: 'Financial Analytics Platform',
    subtitle: 'Real-time market analysis, risk assessment, and investment optimization powered by AI.',
    link: '/fin-app',
    category: 'FINANCE'
  },
  {
    id: 4,
    icon: <Users size={32} />,
    title: 'HR Intelligence Suite',
    subtitle: 'Automate recruitment, employee engagement, and talent management with predictive analytics.',
    link: '/hr-assistant',
    category: 'HUMAN RESOURCES',
    featured: true
  },
  {
    id: 5,
    icon: <BookOpen size={32} />,
    title: 'Adaptive Learning Platform',
    subtitle: 'Personalized educational pathways with real-time feedback and performance tracking.',
    link: '/tutor',
    category: 'EDUCATION',
    stats: {
      value: '95%',
      label: 'Satisfaction Rate'
    }
  },
  {
    id: 6,
    icon: <Stethoscope size={32} />,
    title: 'Healthcare Diagnostics AI',
    subtitle: 'Advanced medical imaging analysis and patient risk assessment for improved outcomes.',
    link: '/healthcare',
    category: 'HEALTHCARE'
  },
  {
    id: 7,
    icon: <BarChart3 size={32} />,
    title: 'Business Intelligence',
    subtitle: 'Transform raw data into actionable insights with automated reporting and predictive modeling.',
    link: '/business-intel',
    category: 'ANALYTICS'
  },
  {
    id: 8,
    icon: <Shield size={32} />,
    title: 'Security & Compliance AI',
    subtitle: 'Proactive threat detection and automated compliance monitoring for enterprise security.',
    link: '/security',
    category: 'SECURITY'
  }
];

const TileGrid: React.FC<{ layout?: 'default' | 'compact' | 'wide' }> = ({ layout = 'default' }) => {
  return (
    <div className={`tile-grid ${layout}`}>
      {tilesData.map((tile) => (
        <div 
          className={`tile ${tile.featured ? 'featured' : ''} ${tile.stats ? 'stats' : ''}`}
          key={tile.id}
        >
          <a href={tile.link} className="tile-link">
            <div className="tile-icon-container">
              <div className="tile-icon">
                {tile.icon}
              </div>
            </div>
            
            <div className="tile-content">
              <span className="tile-category">{tile.category}</span>
              <h3 className="tile-title">{tile.title}</h3>
              <p className="tile-subtitle">{tile.subtitle}</p>
              
              {tile.stats && (
                <div className="tile-stats">
                  <span className="stat-value">{tile.stats.value}</span>
                  <span className="stat-label">{tile.stats.label}</span>
                </div>
              )}
            </div>
            
            <button className="tile-cta">
              Explore Solution
              <span className="tile-cta-icon">→</span>
            </button>
          </a>
        </div>
      ))}
    </div>
  );
};

export default TileGrid;